import { BookingServiceClient } from 'fitbeat.api-clients';
import { UserProfile, WorkoutSettings } from 'fitbeat.gym-core';
import { CardioOrResistance } from 'fitbeat.models';
import React, { FunctionComponent, useState } from 'react';
import { translate } from 'react-i18next';
import { Platform, SafeAreaView, StyleSheet, View } from 'react-native';
import { connect } from 'react-redux';
import { IAppStore, Orientation } from '../reducer';
import { themeStyleMapper } from './HeartRateMonitorSetup';
import { clearPersonaliseSetupWarning, openPersonaliseWorkoutSetupScreen } from '../../Bookings/actions';
import { personaliseSetupScreenSelector, personaliseSetupWarningSelector } from '../../Bookings/selectors';
import {
  PersonaliseSetupWarning,
  PersonaliseWorkoutFooter,
  PersonaliseWorkoutSetup,
} from '../../Bookings/steps/personaliseWorkoutSteps';
import { PersonaliseSetupScreens } from '../../Bookings/steps/personaliseWorkoutSteps/types'
import { getApiConfig } from '../../config';
import Theme, { ThemeType } from '../../theme';
import { AuthTokenBuilder } from '../../utils/authTokenBuilder';
import {
  changeWorkoutSettings,
  closeSetupScreen, closeWorkoutSettings, saveWorkoutSettings,
} from '../actions';
import {
  homeWorkoutSessionSelector, orientationSelector, updatedWorkoutSettingsSelector,
  workoutSettingsHasChangedSelector, workoutSettingsIsUpdatingSelector,
} from '../selectors';
import { ExpoFileSystem } from '../utils';
import { WorkoutAssetHelper } from '../utils/WorkoutAssetHelper';
import { DeviceSetupSubScreenHeader } from './DeviceSetup/DeviceSetupSubScreenHeader';

interface IProps {
  themeType: ThemeType;
  orientation: Orientation;
  userProfile: UserProfile | null;
  currentPersonaliseSetupScreen: PersonaliseSetupScreens | null;
  workoutSettingsHasChanged: any;
  workoutSettingsIsUpdating: any;
  homeWorkoutSession: any;
  updatedWorkoutSettings: any;
  personaliseSetupWarning: PersonaliseSetupWarning | null;
  t(key: string, options?: any): string;
  closeMySetupScreen(): void;
  updateWorkoutSettings(newWorkoutSettings: WorkoutSettings): void;
  openPersonaliseSetupScreen(screen: PersonaliseSetupScreens): void;
  saveMyWorkoutSettings(setDefaultEquipment: boolean): void;
  clearWarning(): void;
}

const MyPersonaliseWorkoutComponent: FunctionComponent<IProps> = ({
  themeType = ThemeType.dark,
  orientation,
  updatedWorkoutSettings,
  homeWorkoutSession,
  userProfile,
  workoutSettingsIsUpdating,
  currentPersonaliseSetupScreen,
  personaliseSetupWarning,
  closeMySetupScreen,
  updateWorkoutSettings,
  openPersonaliseSetupScreen,
  saveMyWorkoutSettings,
  clearWarning,
  t,
}) => {
  const [settingsChanged, setSettingsChanged] = useState<boolean>(false);
  const [settings, setSettings] = useState<any>();
  const [shouldSaveDefaultEquipment, setShouldSaveDefaultEquipment] = useState<boolean>(false);

  const themeStyle = themeStyleMapper[themeType];

  const mappedPersonaliseSetupScreen = {
    [PersonaliseSetupScreens.Overview]: {
      title: '',
    },
    [PersonaliseSetupScreens.SkillLevel]: {
      title: t('changeLevel'),
    },
    [PersonaliseSetupScreens.Equipment]: {
      title: t('changeEquipment'),
    },
    [PersonaliseSetupScreens.WorkoutType]: {
      title: t('changeWorkoutType'),
    },
    [PersonaliseSetupScreens.BodyGroup]: {
      title: t('changeBodyGroup'),
    },
  };

  const goBack = () => {
    currentPersonaliseSetupScreen === PersonaliseSetupScreens.Overview ?
      closeMySetupScreen() : openPersonaliseSetupScreen(PersonaliseSetupScreens.Overview);
  };

  const saveAndClose = () => {
    if (settings.cardioOrResistance === CardioOrResistance.Balance) {
      settings.allowBagContact = false;
    }
    updateWorkoutSettings(settings);
    saveMyWorkoutSettings(shouldSaveDefaultEquipment);
  };

  const closeSetupAndResetOverviewScreen = () => {
    closeMySetupScreen();
    openPersonaliseSetupScreen(PersonaliseSetupScreens.Overview);
  };

  const hasWarning = personaliseSetupWarning === PersonaliseSetupWarning.CustomiseWarning ||
    personaliseSetupWarning === PersonaliseSetupWarning.BodyGroupEquipmentWarning ||
    personaliseSetupWarning === PersonaliseSetupWarning.EquipmentWarning ||
    personaliseSetupWarning === PersonaliseSetupWarning.BagContactBalanceWarning ||
    personaliseSetupWarning === PersonaliseSetupWarning.BodyGroupBalanceWarning;

  const screenHeaderTitle = !hasWarning && currentPersonaliseSetupScreen &&
    mappedPersonaliseSetupScreen[currentPersonaliseSetupScreen] ?
    mappedPersonaliseSetupScreen[currentPersonaliseSetupScreen].title : '';

  return (
    <SafeAreaView style={[styles.outerContainer, themeStyle.container]}>
      <DeviceSetupSubScreenHeader title={screenHeaderTitle} onBackPress={goBack} />

      <View style={styles.container}>
        <PersonaliseWorkoutSetup
          themeType={themeType}
          orientation={orientation}
          gym={homeWorkoutSession.gym}
          workoutSettings={updatedWorkoutSettings}
          userProfile={userProfile}
          onCompleteSettingChange={(workoutSettings: WorkoutSettings, saveDefaultEquipment?: boolean) => {
            setSettings(workoutSettings);
            setSettingsChanged(true);
            setShouldSaveDefaultEquipment(saveDefaultEquipment ?? false)
          }} />

        <PersonaliseWorkoutFooter
          themeType={themeType}
          orientation={orientation}
          currentPersonaliseSetupScreen={currentPersonaliseSetupScreen}
          workoutSettingsIsUpdating={workoutSettingsIsUpdating}
          personaliseSetupWarning={personaliseSetupWarning}
          settingsChanged={settingsChanged}
          goBack={goBack}
          saveAndClose={saveAndClose}
          closeSetupAndResetOverviewScreen={closeSetupAndResetOverviewScreen}
          clearWarning={clearWarning}
          openPersonaliseSetupScreen={openPersonaliseSetupScreen}
          workoutDeviceSetup
        />
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    paddingBottom: 15,
    backgroundColor: Theme.WHITE,
  },
  container: {
    flex: 1,
    paddingHorizontal: 15,
  },
  borderRight: {
    borderRightWidth: Platform.OS === 'ios' ? 0.5 : 1,
    borderRightColor: Theme.GREY,
  },
  borderBottom: {
    borderBottomWidth: 1,
    borderBottomColor: Theme.GREY,
  },
  headerRow: {
    padding: 0,
    justifyContent: 'space-between',
    borderBottomColor: Theme.GREY,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: 15,
    marginHorizontal: -15,
    marginBottom: -15,
  },
});

const mapStateToProps = (state: IAppStore): Partial<IProps> => ({
  userProfile: state.homeWorkouts.userProfile,
  homeWorkoutSession: JSON.parse(JSON.stringify(homeWorkoutSessionSelector(state))),
  updatedWorkoutSettings: updatedWorkoutSettingsSelector(state),
  workoutSettingsHasChanged: workoutSettingsHasChangedSelector(state),
  workoutSettingsIsUpdating: workoutSettingsIsUpdatingSelector(state),
  currentPersonaliseSetupScreen: personaliseSetupScreenSelector(state),
  personaliseSetupWarning: personaliseSetupWarningSelector(state),
  orientation: orientationSelector(state),
});

const mapDispatchToProps = (dispatch: any) => {
  const bookingServiceClient = new BookingServiceClient(
    getApiConfig(),
    fetch,
    new AuthTokenBuilder());
  const workoutAssetHelper = new WorkoutAssetHelper(new ExpoFileSystem());
  return {
    saveMyWorkoutSettings: (setDefaultEquipment: boolean) => dispatch(saveWorkoutSettings(bookingServiceClient, workoutAssetHelper, setDefaultEquipment)),
    updateWorkoutSettings: (newWorkoutSettings: WorkoutSettings) =>
      dispatch(changeWorkoutSettings(JSON.parse(JSON.stringify(newWorkoutSettings)))),
    closeWorkoutSettings: () => dispatch(closeWorkoutSettings()),
    closeMySetupScreen: () => dispatch(closeSetupScreen()),
    openPersonaliseSetupScreen: (screen: PersonaliseSetupScreens) =>
      dispatch(openPersonaliseWorkoutSetupScreen(screen)),
    clearWarning: () => dispatch(clearPersonaliseSetupWarning()),
  };
};
export const MyPersonaliseWorkoutSetup = connect(mapStateToProps, mapDispatchToProps)(
  translate(['homeWorkouts'], { wait: true })(MyPersonaliseWorkoutComponent));
